import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import ToastContext from './toastContext';
import Toast from './Toast';

const AUTO_DISMISS_MS = 4000; // >= 3000ms per design charter
const MAX_VISIBLE = 3;

// Mounts the fixed toast layer (top-center, z-toast) and provides the
// useToast() API. Messages are caller-translated strings; errors that
// require action belong inline (Alert), not here.
//
// ONE TOAST PER MESSAGE. A kind + message already on screen is refreshed in
// place -- its timer restarts and `bump` replays a pulse -- instead of
// stacking a copy. Clicking a blocked nav link ten times used to stack ten
// identical "unsaved changes" pills. Different messages still stack, the
// oldest dropped past MAX_VISIBLE.
//
// `dismissLabel` is the close button's accessible name; ui/ takes its
// strings as props, so the app passes it translated.
export default function ToastProvider({ dismissLabel, children }) {
  const [toasts, setToasts] = useState([]);
  // The list lives in a ref too so push() can decide refresh-vs-add against
  // what is on screen right now, without side effects in a state updater.
  const listRef = useRef([]);
  const nextId = useRef(1);
  const timers = useRef(new Map());

  const { api, dismiss } = useMemo(() => {
    const commit = (next) => {
      listRef.current = next;
      setToasts(next);
    };
    const clearTimer = (id) => {
      clearTimeout(timers.current.get(id));
      timers.current.delete(id);
    };
    const remove = (id) => {
      clearTimer(id);
      commit(listRef.current.filter((item) => item.id !== id));
    };
    const arm = (id) => {
      clearTimer(id);
      timers.current.set(id, setTimeout(() => remove(id), AUTO_DISMISS_MS));
    };

    const push = (kind, message) => {
      const list = listRef.current;
      const existing = list.find((item) => item.kind === kind && item.message === message);
      if (existing) {
        commit(list.map((item) => (item === existing ? { ...item, bump: item.bump + 1 } : item)));
        arm(existing.id);
        return;
      }
      const id = nextId.current;
      nextId.current += 1;
      const kept = list.slice(Math.max(0, list.length - (MAX_VISIBLE - 1)));
      list.filter((item) => !kept.includes(item)).forEach((item) => clearTimer(item.id));
      commit([...kept, { id, kind, message, bump: 0 }]);
      arm(id);
    };

    return {
      api: {
        success: (message) => push('success', message),
        error: (message) => push('error', message),
        info: (message) => push('info', message),
      },
      dismiss: remove,
    };
  }, []);

  useEffect(() => {
    const pending = timers.current;
    return () => pending.forEach(clearTimeout);
  }, []);

  return (
    <ToastContext.Provider value={api}>
      {children}
      {createPortal(
        <div className="toast-layer" role="status" aria-live="polite">
          {toasts.map((item) => (
            // Keyed on bump so a refresh remounts the pill and replays its pulse.
            <Toast
              key={`${item.id}:${item.bump}`}
              kind={item.kind}
              message={item.message}
              bumped={item.bump > 0}
              onDismiss={() => dismiss(item.id)}
              dismissLabel={dismissLabel}
            />
          ))}
        </div>,
        document.body
      )}
    </ToastContext.Provider>
  );
}
